import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import dayjs from 'lib/dayjs';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { useCurrentConversationValue } from 'hooks/business/conversation';
import {
  getErpShipmentStatusLabel,
  getLogisticsShipmentStatusLabel,
} from 'services/i18n/enum/order';
import { TradeDetailLogisticsBlock, TradeDetailTextBlock } from './tradeDetailBlockTypes';

const formatTimestamp = (timestamp: string | number) =>
  dayjs(Number(timestamp)).format('YYYY-MM-DD HH:mm:ss');

const formatTrackingTimestamp = (timestamp?: string | number) => {
  const value = Number(timestamp);

  if (!value) {
    return '';
  }

  return formatTimestamp(value * 1000);
};

const formatMercadoTrackingTimestamp = (timestamp?: string | number) => {
  const value = Number(timestamp);

  if (!value) {
    return '';
  }

  return formatTimestamp(value);
};

const getMercadoTrackingDescription = (
  t: ReturnType<typeof useTranslate>['t'],
  description?: Conversation.Trade.MercadoLogistics['trackingInfo'][number]['description'],
) => {
  if (!description) {
    return '';
  }

  return (
    {
      shipped_date: t('domain.order.field.shipmentDate'),
      returned_date: t('domain.order.logistics.field.returnDate'),
      delivered_date: t('domain.order.logistics.field.deliveryDate'),
      cancelled_date: t('domain.order.logistics.field.deliveryCancellationDate'),
      ready_to_ship_date: t('domain.order.logistics.field.readyToShipDate'),
    }[description] || description
  );
};

const compactRows = (
  rows: TradeDetailLogisticsBlock['detailRows'] = [],
): TradeDetailLogisticsBlock['detailRows'] => rows.filter((row) => row.value);

interface UseTradeLogisticsBlocksOptions {
  sendable?: boolean;
}

export interface UseTradeLogisticsBlocksResult {
  logisticsInfo?: TradeDetailLogisticsBlock;
  thirdLogisticsInfo?: TradeDetailLogisticsBlock;
  latestLogisticsTime?: TradeDetailTextBlock;
}

export const useTradeLogisticsBlocks = (
  trade: Conversation.Trade.TradeWithLogistics,
  options: UseTradeLogisticsBlocksOptions = {},
): UseTradeLogisticsBlocksResult => {
  const { sendable = true } = options;
  const { t } = useTranslate();
  const conversation = useCurrentConversationValue();
  const channel = conversation?.channel;

  return useMemo(() => {
    const { logisticsList, thirdLogisticsList, lazadaLogistics, mercadoLogistics, sellerOrderUrl } =
      trade;
    const blocks: UseTradeLogisticsBlocksResult = {};

    if (logisticsList?.length || thirdLogisticsList?.length) {
      if (logisticsList?.length) {
        const firstLogistics = logisticsList[0];
        const latestTracking = firstLogistics.trackingInfo?.[0];

        blocks.logisticsInfo = {
          id: 'logisticsInfo',
          type: 'logistics',
          title: t('domain.order.logistics.field.status'),
          trackingLabel: getLogisticsShipmentStatusLabel(t, firstLogistics.leChatStatus),
          detailRows: compactRows([
            {
              id: 'trackingNumber',
              label: t('domain.order.logistics.field.trackingNumber'),
              value: firstLogistics.trackingNumber,
              copyable: true,
            },
            {
              id: 'shippingCarrier',
              label: t('domain.order.logistics.field.carrier'),
              value: firstLogistics.shippingCarrier,
            },
            {
              id: 'latestDescription',
              label: t('domain.order.logistics.field.latestDescription'),
              value: latestTracking?.description || '',
            },
            {
              id: 'latestUpdateTime',
              label: t('domain.order.logistics.field.latestUpdateTime'),
              value: formatTrackingTimestamp(latestTracking?.updateTime),
            },
          ]),
          payload: {
            kind: 'common',
            logisticsList,
            showSendLogistics:
              isTiKTokUpperCase(channel) && Boolean(conversation) && Boolean(sendable),
          },
        };
      }

      if (thirdLogisticsList?.length) {
        const firstThirdLogistics = thirdLogisticsList[0];
        const { erpType, originStatus, leChatStatus } = firstThirdLogistics;
        const latestTracking = firstThirdLogistics.trackingInfo?.[0];
        const isHisense = erpType === 'HISENSE';

        if (isHisense) {
          blocks.thirdLogisticsInfo = {
            id: 'thirdLogisticsInfo',
            type: 'logistics',
            title: t('scene.chatWorkspace.orderTab.logistics.title.thirdParty'),
            trackingLabel: getLogisticsShipmentStatusLabel(t, leChatStatus),
            payload: {
              kind: 'hisense',
              logisticsList: thirdLogisticsList,
            },
          };
        } else {
          blocks.thirdLogisticsInfo = {
            id: 'thirdLogisticsInfo',
            type: 'logistics',
            title: t('domain.order.logistics.field.status'),
            trackingLabel:
              erpType && originStatus
                ? getErpShipmentStatusLabel(t, originStatus) || '-'
                : getLogisticsShipmentStatusLabel(t, leChatStatus),
            detailRows: compactRows([
              {
                id: 'trackingNumber',
                label: t('domain.order.logistics.field.trackingNumber'),
                value: firstThirdLogistics.trackingNumber,
                copyable: true,
              },
              {
                id: 'latestDescription',
                label: t('domain.order.logistics.field.latestDescription'),
                value: latestTracking?.description || '',
              },
              {
                id: 'latestUpdateTime',
                label: t('domain.order.logistics.field.latestUpdateTime'),
                value: formatTrackingTimestamp(latestTracking?.updateTime),
              },
            ]),
            payload: {
              kind: 'commonThird',
              logisticsList: thirdLogisticsList,
            },
          };
        }
      }

      return blocks;
    }

    if (lazadaLogistics?.packages?.length) {
      const [firstPackage] = lazadaLogistics.packages;
      const latestTracking = firstPackage.trackingInfo?.[0];

      blocks.logisticsInfo = {
        id: 'logisticsInfo',
        type: 'logistics',
        title: t('domain.order.logistics.field.status'),
        trackingLabel: getLogisticsShipmentStatusLabel(t, firstPackage.leChatStatus),
        detailRows: compactRows([
          {
            id: 'trackingNumber',
            label: t('domain.order.logistics.field.trackingNumber'),
            value: firstPackage.trackingNumber,
            copyable: true,
          },
          {
            id: 'shippingCarrier',
            label: t('domain.order.logistics.field.carrier'),
            value: firstPackage.shippingCarrier,
          },
          {
            id: 'latestDescription',
            label: t('domain.order.logistics.field.latestDescription'),
            value: latestTracking?.description || '',
          },
          {
            id: 'latestUpdateTime',
            label: t('domain.order.logistics.field.latestUpdateTime'),
            value: formatTrackingTimestamp(
              latestTracking?.updateTime || firstPackage.latestTrackingTime,
            ),
          },
        ]),
        payload: {
          kind: 'lazada',
          logisticsList: lazadaLogistics.packages,
        },
      };

      return blocks;
    }

    if (mercadoLogistics) {
      const latestTracking = mercadoLogistics.trackingInfo?.[0];

      blocks.logisticsInfo = {
        id: 'logisticsInfo',
        type: 'logistics',
        title: t('domain.order.logistics.field.status'),
        trackingLabel: getLogisticsShipmentStatusLabel(t, mercadoLogistics.leChatStatus),
        detailRows: compactRows([
          {
            id: 'trackingNumber',
            label: t('domain.order.logistics.field.trackingNumber'),
            value: mercadoLogistics.trackingNumber,
            copyable: true,
          },
          {
            id: 'shippingCarrier',
            label: t('domain.order.logistics.field.carrier'),
            value:
              {
                fulfillment: 'FULL',
                self_service: 'ENVIOS FLEX',
              }[mercadoLogistics.shippingCarrier] || mercadoLogistics.shippingCarrier,
          },
          {
            id: 'latestDescription',
            label: t('domain.order.logistics.field.latestDescription'),
            value: getMercadoTrackingDescription(t, latestTracking?.description),
          },
          {
            id: 'latestUpdateTime',
            label: t('domain.order.logistics.field.latestUpdateTime'),
            value: formatMercadoTrackingTimestamp(latestTracking?.updateTime),
          },
        ]),
        payload: {
          kind: 'mercado',
          logistics: mercadoLogistics,
          sellerOrderUrl,
        },
      };
    }

    return blocks;
  }, [trade, channel, conversation, sendable, t]);
};
